import { DispatchWithoutAction, useCallback, useEffect, useReducer, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { FieldValues } from 'react-hook-form/dist/types'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useMediaQuery, useTheme } from '@mui/material'
import { find, findIndex, get, set } from 'lodash'
import mustache from 'mustache'

import { FlowState, Page, Submit } from 'common/interfaces'
import { useHeapState } from 'context/Heap'
import { useSessionDispatch, useSessionState } from 'context/Session'
import { Location } from 'elements/MapAddress/stateInterface'
import { useCreateFlowSession, useUpdateFlowSession } from 'utils/flowSession'
import isFieldsFilled from 'utils/isFieldFilled'
import { StepperGroup } from 'views/AdjustersHub/ClaimView/components/Stepper/interface'
import { adjustersFlowName } from 'views/AdjustersHub/constants'
import { useOneClaimDispatch, useOneClaimState } from 'views/AdjustersHub/context/oneClaimContext'
import { SelectedClaim } from 'views/AdjustersHub/interfces'
import { useAdjusterFlow } from 'views/AdjustersHub/utils/api'
import getInActiveStepsState from 'views/AdjustersHub/utils/getInActiveStepsState'
import { useCurrentAdjusterPage } from 'views/AdjustersHub/utils/hooks'
import { getClearData } from 'views/Flow/filterData'
import { nextStep } from 'views/Flow/transition'
import useSubmit from 'views/Flow/useSubmit'

import { useFlow, useFlowSlug } from './flow'

type UseLocalStorageReturn<T> = readonly [T, (newValue: T) => void, () => void]

export const useLocalStorage = <T>(key: string, initialValue: T): UseLocalStorageReturn<T> => {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = localStorage.getItem(key)
      return item !== null ? JSON.parse(item) : initialValue
    } catch (error) {
      console.log(error)
      return initialValue
    }
  })

  const setValue = (newValue: T): void => {
    try {
      setStoredValue(newValue)
      localStorage.setItem(key, JSON.stringify(newValue))
    } catch (error) {
      console.error(error)
    }
  }

  const clearValue = (): void => {
    localStorage.removeItem(key)
  }

  return [storedValue, setValue, clearValue] as const
}

type UseBooleanReturn = readonly [boolean, () => void, () => void]

export const useBoolean = (defaultValue: boolean): UseBooleanReturn => {
  const [state, setState] = useState<boolean>(defaultValue)
  const setTrue = useCallback(() => setState(true), [])
  const setFalse = useCallback(() => setState(false), [])
  return [state, setTrue, setFalse]
}

export const useToggle = (defaultValue: boolean): [boolean, DispatchWithoutAction] => {
  return useReducer((state: boolean): boolean => !state, defaultValue)
}

export function useCurrentPage(): Page {
  const { data: flow } = useFlow()
  const { currentPage } = useSessionState()

  if (flow === undefined) {
    throw new Error('useCurrentPage needs fetched flow to get current page')
  }

  const page = flow.pages.find(({ id }) => id === currentPage)

  if (page === undefined) {
    throw new Error('useCurrentPage has no found current page via id')
  }

  return page
}

export type MustacheRender = (template: string, element?: Record<string, any>) => string
export type MustacheCustomRender = (
  template: string,
  element?: Record<string, any>
) => string | number | boolean | any[] | Record<string, any>

type TemplateFunction = (value: string, render: (t: string) => string) => string

const templateFunctions = {
  date: (): TemplateFunction => {
    return (value, render) => {
      const timestamp = render(value).trim()
      return new Date(timestamp).toLocaleDateString()
    }
  },
  time: (): TemplateFunction => {
    const options = { hour: 'numeric', minute: 'numeric' } as const
    return (value, render) => {
      const timestamp = render(value).trim()
      return new Date(timestamp).toLocaleTimeString([], options)
    }
  },
}

export function useTemplate(): MustacheRender {
  const { watch } = useFormContext()
  const { isSuccess, data } = useSubmit()

  const sessionFlowId = useFlowSessionId()
  const heap = useHeapState()
  const session = watch()
  const [selectedClaim] = useLocalStorage('selectedClaim', {})

  return (template, state = {}) => {
    const context = {
      heap,
      state,
      session,
      selectedClaim,
      sessionFlowId,
      response: isSuccess ? data : undefined,
      ...templateFunctions,
    }

    return mustache.render(
      template,
      context,
      {},
      {
        escape: (text) => text,
      }
    )
  }
}

export function useTemplateCustomRender(): MustacheCustomRender {
  const { watch } = useFormContext()
  const { isSuccess, data } = useSubmit()

  const sessionFlowId = useFlowSessionId()
  const heap = useHeapState()
  const session = watch()
  const [selectedClaim] = useLocalStorage('selectedClaim', {})

  return (template, state = {}) => {
    const context = {
      heap,
      state,
      session,
      selectedClaim,
      sessionFlowId,
      response: isSuccess ? data : undefined,
      ...templateFunctions,
    }

    return get(context, template)
  }
}

export function useFlowSessionId(): string {
  const { pathname } = useLocation()
  if (pathname.includes(adjustersFlowName)) {
    return ''
  }

  const { data: flow } = useFlow()
  const { data: flowSession } = useCreateFlowSession()

  return flow!.flow_session.id ?? flowSession?.id
}

export function useNext(): () => void {
  const { pathname } = useLocation()
  const [dispatch] = useSessionDispatch()

  const { trigger } = useFormContext()

  if (pathname.includes(adjustersFlowName)) {
    const { currentPage } = useSessionState()
    const { data: flowData } = useAdjusterFlow()
    const [{ flowKey }] = useLocalStorage<SelectedClaim>(
      'selectedClaim',
      {} as unknown as SelectedClaim
    )
    const dispatchOneClaim = useOneClaimDispatch()

    const { localStore } = useOneClaimState()

    const step = find(flowData?.[flowKey].pages, (item) => {
      if (currentPage) {
        return item.id === currentPage
      }

      return flowData?.[flowKey].start_page.id === item.id
    })!.transitions[0].destination

    const setStepsState = async (): Promise<any> => {
      if (localStore !== null) {
        const stepsStateLS: StepperGroup | null = await localStore.getItem('stepsState')

        const stepsState = getInActiveStepsState(stepsStateLS as StepperGroup)

        for (const currentStep of stepsState) {
          const inGroupId = currentStep.id

          if (inGroupId === step) {
            currentStep.isActive = true
            break
          }

          const stepIndex = findIndex(currentStep.steps, { id: step })

          if (stepIndex >= 0) {
            currentStep.isActive = true
            currentStep.steps![stepIndex].isActive = true
            break
          }
        }

        dispatchOneClaim({ type: 'setStepsState', stepsState })
      }
    }

    return () => {
      void trigger().then((isValid) => {
        if (!isValid) {
          return
        }

        void setStepsState()
        dispatch({ step, type: 'pushStep' })
        window.scrollTo(0, 0)
      })
    }
  }

  const flowSessionId = useFlowSessionId()
  const { mutate: updateFlowSession } = useUpdateFlowSession()

  const flowState = useFlowState()
  const render = useTemplateCustomRender()
  const currentPage = useCurrentPage()

  return function (): void {
    void trigger().then((isValid) => {
      if (!isValid) {
        return
      }

      const step = nextStep(currentPage.transitions, render)

      if (step !== null) {
        updateFlowSession(
          {
            id: flowSessionId,
            current_page: step,
            ...flowState,
          },
          {
            onSettled: () => {
              dispatch({ step, type: 'pushStep' })
              window.scrollTo(0, 0)
            },
          }
        )
      }
    })
  }
}

export function useGeolocation(): [Location | null, GeolocationPositionError | Error | null] {
  const [position, setPosition] = useState<Location | null>(null)
  const [error, setError] = useState<GeolocationPositionError | Error | null>(null)

  const onChange: PositionCallback = ({ coords }) => {
    const position: Location = { lat: coords.latitude, lng: coords.longitude }
    setPosition(position)
  }

  const onError: PositionErrorCallback = (err) => {
    setError(err)
  }

  const options: PositionOptions = {
    maximumAge: 60 * 60 * 1000,
    enableHighAccuracy: false,
  }

  useEffect(() => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(onChange, onError, options)
    } else {
      setError(new Error('Geolocation is not supported'))
    }
  }, [])

  return [position, error]
}

export function useIsPhone(): boolean {
  const theme = useTheme()
  return useMediaQuery(theme.breakpoints.down('sm'))
}

export function useFlowState(): Submit {
  const flowSlug = useFlowSlug()

  let flow: any = null
  let selectedClaim
  const { pathname } = useLocation()
  if (pathname.includes(adjustersFlowName)) {
    ;[selectedClaim] = useLocalStorage<SelectedClaim>(
      'selectedClaim',
      {} as unknown as SelectedClaim
    )
    const { data } = useAdjusterFlow()
    flow = data![selectedClaim.flowKey]
  } else {
    const { data } = useFlow()
    flow = data
  }

  const { getValues } = useFormContext()

  const [lsValue] = useLocalStorage(`${flowSlug}/metadata`, {})
  const [sessionFlowId] = useLocalStorage<string | null>(`${flowSlug}/sessionFlowId`, null)
  const heap = useHeapState()
  const { history, path, currentPage } = useSessionState()

  const values = getValues()

  return {
    flow: flow.id,
    path: [...path, currentPage],
    full_path: [...history, currentPage],
    data: getClearData(values, flow, [...path, currentPage]),
    full_data: {
      ...values,
      ...(selectedClaim !== undefined && { airtable: selectedClaim }),
    },
    heap,
    metadata: {
      ...lsValue,
      ...(sessionFlowId != null && { flow_session_id: sessionFlowId }),
    },
  }
}

interface SearchParams {
  s_id?: string
  preview_id?: string
  fields?: Record<string, string>
  meta?: Record<string, string>
  heap?: Record<string, string>
}

export function useSearchParamsObj(): SearchParams {
  if (location.search === '') {
    return {}
  }

  const [searchParams] = useSearchParams()

  const objWithSearchParams: SearchParams = Array.from(searchParams.entries()).reduce(
    (acc, [key, val]) => {
      return set(acc, key, val)
    },
    {}
  )

  return objWithSearchParams
}

export function useIsOnline(): boolean {
  const [isOnline, setIsOnline] = useState(navigator.onLine)

  useEffect(() => {
    const handleStatusChange = (): void => {
      setIsOnline(navigator.onLine)
    }

    window.addEventListener('online', handleStatusChange)

    window.addEventListener('offline', handleStatusChange)

    return () => {
      window.removeEventListener('online', handleStatusChange)
      window.removeEventListener('offline', handleStatusChange)
    }
  }, [isOnline])

  return isOnline
}

export function useSetIsDisabled(
  values: FieldValues,
  allRequiredFields?: FlowState['required_fields']
): void {
  const [dispatchSession] = useSessionDispatch()

  useEffect(() => {
    if (allRequiredFields !== undefined && allRequiredFields?.length > 0) {
      const isAllFilled = isFieldsFilled(allRequiredFields, values, 'every')
      dispatchSession({ type: 'setIsSubmitDisabled', isSubmitDisabled: !isAllFilled })
    }
  }, [allRequiredFields, values])
}
